/**
 * The extra evidence a Metadata-only (enrichReferenceLight) run sends beside the
 * stored-ref payload — the two items the 2026-10-28 diagnostic found missing:
 *
 *   - `productUrl`: the product page URL as a string (its scraped TEXT already
 *     goes as SOURCE URL CONTENT when "Use product URL" is ticked);
 *   - `brandTimeline`: up to 15 of the brand's timeline PRODUCT events, year +
 *     title only — a dated launch ("1957 — Railmaster introduced") is the
 *     strongest collection-level dating evidence there is, and the description
 *     text would only add tokens.
 *
 * The callable passes both through for metadata runs only (see
 * functions/src/enrichReferenceProfile.ts `METADATA_EXTRA_KEYS`).
 */
import type { TimelineEvent } from '@watchkeeper/shared-types';
import { firstYear } from './calibreLineageLogic';

export const MAX_TIMELINE_EVENTS = 15;

export interface TimelineYearEvent {
  year: number;
  title: string;
}

/** The brand's product events with a readable year, oldest first, capped. */
export function brandProductTimeline(
  history: readonly TimelineEvent[] | undefined,
  max = MAX_TIMELINE_EVENTS,
): TimelineYearEvent[] {
  return (history ?? [])
    .filter((e) => e.category === 'product')
    .map((e) => ({ year: firstYear(e.date), title: (e.title ?? '').trim() }))
    .filter((e): e is TimelineYearEvent => e.year !== null && e.title !== '')
    .sort((a, b) => a.year - b.year || a.title.localeCompare(b.title))
    .slice(0, Math.max(0, max));
}

/** The metadata-only additions for one ref. Keys are omitted when empty. */
export function metadataExtras(
  ref: { productUrl?: string; source?: { url?: string } },
  timeline: readonly TimelineYearEvent[],
): { productUrl?: string; brandTimeline?: TimelineYearEvent[] } {
  const url = (ref.productUrl ?? ref.source?.url ?? '').trim();
  return {
    ...(url ? { productUrl: url } : {}),
    ...(timeline.length > 0 ? { brandTimeline: [...timeline] } : {}),
  };
}
